import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { deepCamelCaseKeys } from 'src/utils/deepCamelCaseKeys';
import { sessionStore } from 'src/store/sessionStore';
import { auth } from 'src/config/firebase';
import { FinalProgrammePlan } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGenerated/types';

export interface FetchedProgramPlan extends FinalProgrammePlan {
  id: string;
  docPath: string;
  clientId: string;
  trainerId: string;
  generatorType?: string;
  type?: string;
}

export const fetchTrainerProgramPlanList = async (
  trainerId: string,
  filters: { isActive?: string; clientId?: string; sortFilter?: string; searchQuery?: string },
): Promise<FetchedProgramPlan[]> => {
  if (!trainerId) {
    throw new Error('Trainer ID is missing');
  }

  const queryParams = new URLSearchParams({ trainerId });
  if (filters.isActive) queryParams.append('isActive', filters.isActive);
  if (filters.clientId) queryParams.append('clientId', filters.clientId);
  if (filters.sortFilter) queryParams.append('sortBy', filters.sortFilter);
  if (filters.searchQuery) queryParams.append('searchQuery', filters.searchQuery);
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_PROGRAM_PLAN_LIST}?${queryParams.toString()}`;

  const response = await fetch(apiUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch trainer programs: ${response.status}`);
  }

  const res = await response.json();
  const parsedData = deepCamelCaseKeys(res.data);
  return parsedData as unknown as FetchedProgramPlan[];
};

export function useGetTrainerProgramPlanList(
  filters: { isActive?: string; clientId?: string; sortFilter?: string; searchQuery?: string } = {},
) {
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid || auth.currentUser?.uid;

  return useQuery({
    queryKey: [
      'trainer-program-plan-list',
      trainerId,
      filters.isActive,
      filters.clientId,
      filters.sortFilter,
      filters.searchQuery,
    ],
    queryFn: () => fetchTrainerProgramPlanList(trainerId as string, filters),
    enabled: !!trainerId,
  });
}
